import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Tab,
  Tabs,
  Badge,
  Card,
  Accordion,
  Alert,
} from 'react-bootstrap';
import {
  FaLock,
  FaLockOpen,
  FaFileAlt,
  FaHistory,
  FaClipboardList,
  FaWindowClose,
  FaUser,
  FaClock,
  FaCheckSquare,
  FaSquare,
  FaUsers,
  FaHome,
  FaFile,
  FaEye,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaExternalLinkAlt,
  FaFilePdf,
  FaFileImage,
  FaMoneyBillWave,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as api from '~/Services/Service';
import { obterDocumentoDigitalCessao, downloadDocumentoCessao, cessaoInfosBoleto, desbloquearSolicitacaoCessao } from '~/Services/Service';
import Loading from '~/Components/Loading';
import {
  formatCpfCnpj,
  normalizeCpfCnpj,
  MOTIVOS_REPROVACAO,
  MAPA_CAMPOS_PESSOA,
  MAPA_CAMPOS_LOTE,
  campoEditavelCliente,
} from './constants';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const limparZerosPercentual = texto => {
  if (!texto) return texto;
  return texto.replace(/(\d+)\.0+(%)/g, '$1$2').replace(/(\d+\.\d*?)0+(%)/g, '$1$2');
};

const ESTADOS_CIVIS_MAP = {
  C: 'Casado(a)',
  S: 'Solteiro(a)',
  D: 'Divorciado(a)',
  V: 'Viúvo(a)',
  U: 'União Estável',
  P: 'Separado(a)',
  E: 'Separado(a)',
  B: 'Separado(a)',
};

const getPessoaKeys = (pessoa, tipo) => {
  if (!pessoa) return [];
  const baseKey = tipo + '_' + (pessoa.codigo || 0);
  const keys = [baseKey + '_nome', baseKey + '_cpf'];
  if (pessoa.tipo_pessoa !== '1') {
    keys.push(baseKey + '_rg');
    keys.push(baseKey + '_estado_civil');
    keys.push(baseKey + '_data_nascimento');
    keys.push(baseKey + '_profissao');
    keys.push(baseKey + '_nacionalidade');
  }
  keys.push(baseKey + '_email');
  keys.push(baseKey + '_telefone');
  keys.push(baseKey + '_endereco');
  if (pessoa.nome_conjuge) {
    keys.push(baseKey + '_conjuge');
  }
  return keys;
};

const getLoteKeys = () => {
  return [
    'lote_empreendimento',
    'lote_quadra',
    'lote_lote',
    'lote_area_total',
    'lote_cidade',
    'lote_tipo_cessao',
  ];
};

const getSolicitacaoKeys = () => {
  return ['solicitacao_ligacao_agua', 'solicitacao_construcao'];
};

const CampoConferencia = ({
  label,
  valor,
  conferido,
  onConferir,
  temAnexo,
  anexoStatus,
  onVerAnexo,
  editavelCliente = false,
  invalido = false,
  motivoInvalidez = '',
  onMarcarInvalido,
}) => {
  const podeConferir = !temAnexo || anexoStatus === 2 || anexoStatus === 3;

  return (
    <div
      className={`d-flex align-items-start mb-2 py-2 border-bottom ${
        invalido ? 'bg-danger-light' : ''
      }`}
      style={{
        backgroundColor: invalido ? '#f8d7da' : 'transparent',
        borderRadius: invalido ? '4px' : '0',
        padding: invalido ? '8px' : undefined,
      }}
    >
      <div className="mr-2" style={{ minWidth: 24 }}>
        <Form.Check
          type="checkbox"
          checked={conferido}
          onChange={e => onConferir(e.target.checked)}
          disabled={!podeConferir}
          className="mt-1"
        />
      </div>
      <div className="flex-grow-1">
        <small className="text-muted d-block">
          {label}
          {editavelCliente && (
            <Badge
              variant="info"
              className="ml-2"
              style={{ fontSize: '0.65rem' }}
            >
              Editável pelo cliente
            </Badge>
          )}
          {invalido && !editavelCliente && (
            <Badge
              variant="danger"
              className="ml-2"
              style={{ fontSize: '0.65rem' }}
            >
              Não editável - Bloqueia
            </Badge>
          )}
        </small>
        <span
          className={conferido ? 'text-success' : invalido ? 'text-danger' : ''}
        >
          {valor || '-'}
        </span>
        {invalido && motivoInvalidez && (
          <small className="d-block text-danger mt-1">
            <FaTimes size={10} className="mr-1" />
            {motivoInvalidez}
          </small>
        )}
      </div>

      {onMarcarInvalido && conferido && (
        <div className="ml-2">
          <Button
            variant={invalido ? 'danger' : 'outline-danger'}
            size="sm"
            onClick={() => onMarcarInvalido(!invalido)}
            title={
              invalido ? 'Desmarcar como inválido' : 'Marcar como inválido'
            }
          >
            <FaTimes size={12} />
          </Button>
        </div>
      )}
      {temAnexo && (
        <div className="ml-2 d-flex align-items-center">
          <Button
            variant="link"
            size="sm"
            onClick={onVerAnexo}
            className="p-0 mr-2"
          >
            <FaEye /> Ver
          </Button>
          {anexoStatus === 2 && (
            <Badge variant="success">
              <FaCheck /> Aprovado
            </Badge>
          )}
          {anexoStatus === 3 && (
            <Badge variant="danger">
              <FaTimes /> Reprovado
            </Badge>
          )}
          {(!anexoStatus || anexoStatus === 1) && (
            <Badge variant="warning">Pendente</Badge>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentoItem = ({
  documento,
  onVerAnexo,
  validacao,
  onValidar,
  disabled,
}) => {
  const getIconeDocumento = caminho => {
    if (!caminho) return <FaFile />;
    const ext = caminho
      .split('.')
      .pop()
      .toLowerCase();
    if (ext === 'pdf') return <FaFilePdf className="text-danger" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext))
      return <FaFileImage className="text-primary" />;
    return <FaFile />;
  };

  const statusAtual = validacao;

  return (
    <div
      className={`d-flex align-items-center justify-content-between p-3 rounded mb-2 ${
        statusAtual === true
          ? 'bg-success-light border-success'
          : statusAtual === false
          ? 'bg-danger-light border-danger'
          : 'bg-warning-light border-warning'
      }`}
      style={{
        border: '2px solid',
        borderColor:
          statusAtual === true
            ? '#28a745'
            : statusAtual === false
            ? '#dc3545'
            : '#ffc107',
        backgroundColor:
          statusAtual === true
            ? '#d4edda'
            : statusAtual === false
            ? '#f8d7da'
            : '#fff3cd',
      }}
    >
      <div className="d-flex align-items-center flex-grow-1">
        <span className="mr-3" style={{ fontSize: '1.5rem' }}>
          {getIconeDocumento(documento.caminho_arquivo)}
        </span>
        <div className="flex-grow-1">
          <strong className="d-block">
            {documento.tipo_documento_descricao || 'Documento'}
          </strong>
          <small className="text-muted">
            {documento.caminho_arquivo
              ? documento.caminho_arquivo
                  .split('\\')
                  .pop()
                  .split('/')
                  .pop()
              : 'Arquivo não disponível'}
          </small>
        </div>
      </div>

      <div className="d-flex align-items-center">
        <div className="mr-3 text-center" style={{ minWidth: 80 }}>
          {statusAtual === true && (
            <Badge variant="success" className="px-2 py-1">
              <FaCheck className="mr-1" /> Aprovado
            </Badge>
          )}
          {statusAtual === false && (
            <Badge variant="danger" className="px-2 py-1">
              <FaTimes className="mr-1" /> Reprovado
            </Badge>
          )}
          {statusAtual === null || statusAtual === undefined ? (
            <Badge variant="warning" className="px-2 py-1">
              Pendente
            </Badge>
          ) : null}
        </div>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => onVerAnexo(documento)}
          title="Visualizar documento"
          className="mr-2"
        >
          <FaEye className="mr-1" />
          Ver
        </Button>

        <div className="btn-group">
          <Button
            variant={statusAtual === true ? 'success' : 'outline-success'}
            size="sm"
            onClick={() => onValidar(documento.codigo, true)}
            disabled={disabled}
            title="Aprovar documento"
          >
            <FaCheck />
          </Button>
          <Button
            variant={statusAtual === false ? 'danger' : 'outline-danger'}
            size="sm"
            onClick={() => onValidar(documento.codigo, false)}
            disabled={disabled}
            title="Invalidar documento"
          >
            <FaTimes />
          </Button>
        </div>
      </div>
    </div>
  );
};

const SecaoPessoa = ({
  pessoa,
  titulo,
  tipo,
  conferencias,
  onConferir,
  onVerAnexo,
  documentosValidacao,
  onValidarDocumento,
  disabled,
  camposInvalidos = {},
  onMarcarInvalido,
}) => {
  if (!pessoa) return null;

  const baseKey = tipo + '_' + (pessoa.codigo || 0);
  const keys = getPessoaKeys(pessoa, tipo);
  const conferido = keys.every(k => conferencias[k]);
  const documentos = pessoa.documentos || [];

  const handleCheckAll = () => {
    const newValue = !conferido;
    keys.forEach(k => onConferir(k, newValue));
  };

  const formatarEndereco = p => {
    const partes = [];
    if (p.logradouro) partes.push(p.logradouro);
    if (p.numero) partes.push(p.numero);
    if (p.complemento) partes.push(p.complemento);
    if (p.bairro) partes.push(p.bairro);
    if (p.cidade) partes.push(p.cidade);
    if (p.estado) partes.push(p.estado);
    if (p.cep) partes.push('CEP: ' + p.cep);
    return partes.join(', ') || '-';
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            checked={conferido}
            onChange={handleCheckAll}
            className="mr-2"
          />
          <strong>
            <FaUser className="mr-2" />
            {titulo}
            {pessoa.tipo && pessoa.tipo !== 'Principal' && (
              <Badge variant="secondary" className="ml-2">
                {pessoa.tipo}
              </Badge>
            )}
          </strong>
        </div>
        <div className="d-flex align-items-center">
          {documentos.length > 0 && (
            <Badge variant="info" className="mr-2">
              <FaFile size={10} className="mr-1" />
              {documentos.length} doc(s)
            </Badge>
          )}
          {pessoa.tipo_pessoa === '1' && (
            <Badge variant="warning">Pessoa Jurídica</Badge>
          )}
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        <Row>
          <Col md={6}>
            <CampoConferencia
              label="Nome"
              valor={pessoa.nome}
              conferido={conferencias[baseKey + '_nome']}
              onConferir={val => onConferir(baseKey + '_nome', val)}
              editavelCliente={false}
              invalido={camposInvalidos[baseKey + '_nome']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido(baseKey + '_nome', val)
              }
            />
          </Col>
          <Col md={6}>
            <CampoConferencia
              label="CPF/CNPJ"
              valor={formatCpfCnpj(pessoa.cpf)}
              conferido={conferencias[baseKey + '_cpf']}
              onConferir={val => onConferir(baseKey + '_cpf', val)}
              editavelCliente={false}
              invalido={camposInvalidos[baseKey + '_cpf']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido(baseKey + '_cpf', val)
              }
            />
          </Col>
        </Row>

        {pessoa.tipo_pessoa !== '1' && (
          <>
            <Row>
              <Col md={4}>
                <CampoConferencia
                  label="RG"
                  valor={pessoa.rg}
                  conferido={conferencias[baseKey + '_rg']}
                  onConferir={val => onConferir(baseKey + '_rg', val)}
                  editavelCliente={false}
                  invalido={camposInvalidos[baseKey + '_rg']}
                  onMarcarInvalido={val =>
                    onMarcarInvalido && onMarcarInvalido(baseKey + '_rg', val)
                  }
                />
              </Col>
              <Col md={4}>
                <CampoConferencia
                  label="Estado Civil"
                  valor={
                    pessoa.estado_civil_descricao ||
                    ESTADOS_CIVIS_MAP[pessoa.estado_civil] ||
                    pessoa.estado_civil
                  }
                  conferido={conferencias[baseKey + '_estado_civil']}
                  onConferir={val => onConferir(baseKey + '_estado_civil', val)}
                  editavelCliente={false}
                  invalido={camposInvalidos[baseKey + '_estado_civil']}
                  onMarcarInvalido={val =>
                    onMarcarInvalido &&
                    onMarcarInvalido(baseKey + '_estado_civil', val)
                  }
                />
              </Col>
              <Col md={4}>
                <CampoConferencia
                  label="Data de Nascimento"
                  valor={
                    pessoa.data_nascimento
                      ? moment(pessoa.data_nascimento).format('DD/MM/YYYY')
                      : '-'
                  }
                  conferido={conferencias[baseKey + '_data_nascimento']}
                  onConferir={val =>
                    onConferir(baseKey + '_data_nascimento', val)
                  }
                  editavelCliente={false}
                  invalido={camposInvalidos[baseKey + '_data_nascimento']}
                  onMarcarInvalido={val =>
                    onMarcarInvalido &&
                    onMarcarInvalido(baseKey + '_data_nascimento', val)
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <CampoConferencia
                  label="Profissão"
                  valor={pessoa.profissao}
                  conferido={conferencias[baseKey + '_profissao']}
                  onConferir={val => onConferir(baseKey + '_profissao', val)}
                  editavelCliente={false}
                  invalido={camposInvalidos[baseKey + '_profissao']}
                  onMarcarInvalido={val =>
                    onMarcarInvalido &&
                    onMarcarInvalido(baseKey + '_profissao', val)
                  }
                />
              </Col>
              <Col md={6}>
                <CampoConferencia
                  label="Nacionalidade"
                  valor={pessoa.nacionalidade}
                  conferido={conferencias[baseKey + '_nacionalidade']}
                  onConferir={val =>
                    onConferir(baseKey + '_nacionalidade', val)
                  }
                  editavelCliente={false}
                  invalido={camposInvalidos[baseKey + '_nacionalidade']}
                  onMarcarInvalido={val =>
                    onMarcarInvalido &&
                    onMarcarInvalido(baseKey + '_nacionalidade', val)
                  }
                />
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col md={6}>
            <CampoConferencia
              label="E-mail"
              valor={pessoa.email}
              conferido={conferencias[baseKey + '_email']}
              onConferir={val => onConferir(baseKey + '_email', val)}
              editavelCliente={true}
              invalido={camposInvalidos[baseKey + '_email']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido(baseKey + '_email', val)
              }
            />
          </Col>
          <Col md={6}>
            <CampoConferencia
              label="Telefone"
              valor={
                pessoa.telefone ||
                pessoa.telefone_celular ||
                pessoa.telefone_comercial
              }
              conferido={conferencias[baseKey + '_telefone']}
              onConferir={val => onConferir(baseKey + '_telefone', val)}
              editavelCliente={true}
              invalido={camposInvalidos[baseKey + '_telefone']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido(baseKey + '_telefone', val)
              }
            />
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <CampoConferencia
              label="Endereço"
              valor={formatarEndereco(pessoa)}
              conferido={conferencias[baseKey + '_endereco']}
              onConferir={val => onConferir(baseKey + '_endereco', val)}
              editavelCliente={true}
              invalido={camposInvalidos[baseKey + '_endereco']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido(baseKey + '_endereco', val)
              }
            />
          </Col>
        </Row>

        {pessoa.nome_conjuge && (
          <Row>
            <Col md={12}>
              <CampoConferencia
                label="Cônjuge"
                valor={pessoa.nome_conjuge}
                conferido={conferencias[baseKey + '_conjuge']}
                onConferir={val => onConferir(baseKey + '_conjuge', val)}
                editavelCliente={false}
                invalido={camposInvalidos[baseKey + '_conjuge']}
                onMarcarInvalido={val =>
                  onMarcarInvalido &&
                  onMarcarInvalido(baseKey + '_conjuge', val)
                }
              />
            </Col>
          </Row>
        )}

        {documentos.length > 0 && (
          <div className="mt-3 pt-3 border-top">
            <h6 className="mb-3 d-flex align-items-center">
              <FaFile className="mr-2 text-primary" />
              <span>
                Documentos de{' '}
                {pessoa.nome ? pessoa.nome.split(' ')[0] || titulo : titulo}
              </span>
              <Badge variant="primary" className="ml-2">
                {documentos.length}
              </Badge>
            </h6>
            {documentos.map((doc, idx) => (
              <DocumentoItem
                key={doc.codigo || idx}
                documento={doc}
                onVerAnexo={onVerAnexo}
                validacao={documentosValidacao[doc.codigo]}
                onValidar={onValidarDocumento}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const SecaoLote = ({
  lote,
  conferencias,
  onConferir,
  onVerAnexo,
  documentosValidacao,
  onValidarDocumento,
  disabled,
  camposInvalidos = {},
  onMarcarInvalido,
}) => {
  if (!lote) return null;

  const keys = getLoteKeys();
  const conferido = keys.every(k => conferencias[k]);
  const documentos = lote.documentos || [];

  const handleCheckAll = () => {
    const newValue = !conferido;
    keys.forEach(k => onConferir(k, newValue));
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            checked={conferido}
            onChange={handleCheckAll}
            className="mr-2"
          />
          <strong>
            <FaHome className="mr-2" />
            Dados do Lote / Imóvel
          </strong>
        </div>
        {documentos.length > 0 && (
          <Badge variant="info">
            <FaFile size={10} className="mr-1" />
            {documentos.length} doc(s) do imóvel
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        <Row>
          <Col md={6}>
            <CampoConferencia
              label="Empreendimento"
              valor={lote.empreendimento}
              conferido={conferencias['lote_empreendimento']}
              onConferir={val => onConferir('lote_empreendimento', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_empreendimento']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_empreendimento', val)
              }
            />
          </Col>
          <Col md={3}>
            <CampoConferencia
              label="Quadra"
              valor={lote.quadra}
              conferido={conferencias['lote_quadra']}
              onConferir={val => onConferir('lote_quadra', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_quadra']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_quadra', val)
              }
            />
          </Col>
          <Col md={3}>
            <CampoConferencia
              label="Lote"
              valor={lote.lote}
              conferido={conferencias['lote_lote']}
              onConferir={val => onConferir('lote_lote', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_lote']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_lote', val)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <CampoConferencia
              label="Área Total"
              valor={lote.area_total}
              conferido={conferencias['lote_area_total']}
              onConferir={val => onConferir('lote_area_total', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_area_total']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_area_total', val)
              }
            />
          </Col>
          <Col md={4}>
            <CampoConferencia
              label="Cidade/UF"
              valor={lote.cidade ? lote.cidade + '/' + lote.estado : '-'}
              conferido={conferencias['lote_cidade']}
              onConferir={val => onConferir('lote_cidade', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_cidade']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_cidade', val)
              }
            />
          </Col>
          <Col md={4}>
            <CampoConferencia
              label="Tipo Cessão"
              valor={lote.tipo_cessao}
              conferido={conferencias['lote_tipo_cessao']}
              onConferir={val => onConferir('lote_tipo_cessao', val)}
              editavelCliente={false}
              invalido={camposInvalidos['lote_tipo_cessao']}
              onMarcarInvalido={val =>
                onMarcarInvalido && onMarcarInvalido('lote_tipo_cessao', val)
              }
            />
          </Col>
        </Row>

        {documentos.length > 0 && (
          <div className="mt-3 pt-3 border-top">
            <h6 className="mb-3 d-flex align-items-center">
              <FaFile className="mr-2 text-info" />
              <span>Documentos do Imóvel</span>
              <Badge variant="info" className="ml-2">
                {documentos.length}
              </Badge>
            </h6>
            {documentos.map((doc, idx) => (
              <DocumentoItem
                key={doc.codigo || idx}
                documento={doc}
                onVerAnexo={onVerAnexo}
                validacao={documentosValidacao[doc.codigo]}
                onValidar={onValidarDocumento}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const SecaoSolicitacao = ({
  detalhes,
  conferencias,
  onConferir,
  camposInvalidos = {},
  onMarcarInvalido,
}) => {
  if (!detalhes) return null;

  const keys = getSolicitacaoKeys();
  const conferido = keys.every(k => conferencias[k]);

  const handleCheckAll = () => {
    const newValue = !conferido;
    keys.forEach(k => onConferir(k, newValue));
  };

  const formatarSimNao = valor => {
    if (valor === true) return 'Sim';
    if (valor === false) return 'Não';
    return '-';
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            checked={conferido}
            onChange={handleCheckAll}
            className="mr-2"
          />
          <strong>
            <FaClipboardList className="mr-2" />
            Dados da Solicitação
          </strong>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        <Row>
          <Col md={4}>
            <CampoConferencia
              label="Possui Ligação de Água?"
              valor={formatarSimNao(detalhes.possui_ligacao_agua)}
              conferido={conferencias['solicitacao_ligacao_agua']}
              onConferir={val => onConferir('solicitacao_ligacao_agua', val)}
              editavelCliente={false}
              invalido={camposInvalidos['solicitacao_ligacao_agua']}
              onMarcarInvalido={val =>
                onMarcarInvalido &&
                onMarcarInvalido('solicitacao_ligacao_agua', val)
              }
            />
          </Col>
          <Col md={4}>
            <CampoConferencia
              label="Possui Construção?"
              valor={formatarSimNao(detalhes.possui_construcao)}
              conferido={conferencias['solicitacao_construcao']}
              onConferir={val => onConferir('solicitacao_construcao', val)}
              editavelCliente={false}
              invalido={camposInvalidos['solicitacao_construcao']}
              onMarcarInvalido={val =>
                onMarcarInvalido &&
                onMarcarInvalido('solicitacao_construcao', val)
              }
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const ModalDetalhes = ({ show, onHide, solicitacao, onStatusChange, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('formulario');
  const [historico, setHistorico] = useState([]);
  const [isLoadingHistorico, setIsLoadingHistorico] = useState(false);
  const [selectedHistorico, setSelectedHistorico] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [isLoadingDetalhes, setIsLoadingDetalhes] = useState(false);
  const [conferencias, setConferencias] = useState({});

  const [decisao, setDecisao] = useState(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [observacao, setObservacao] = useState('');
  const [enviarEmail, setEnviarEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [documentosValidacao, setDocumentosValidacao] = useState({});
  const [camposInvalidos, setCamposInvalidos] = useState({});
  const [documentoDigital, setDocumentoDigital] = useState(null);
  const [isLoadingDocumento, setIsLoadingDocumento] = useState(false);
  const [isDesbloqueando, setIsDesbloqueando] = useState(false);
  const [infosBoleto, setInfosBoleto] = useState(null);
  const [isLoadingInfosBoleto, setIsLoadingInfosBoleto] = useState(false);

  const isInativa = solicitacao && solicitacao.status === 0;

  useEffect(() => {
    if (show && solicitacao) {
      setSelectedHistorico(null);
      setConferencias({});
      setDecisao(null);
      setMotivoReprovacao('');
      setObservacao('');
      setEnviarEmail(true);
      setDocumentosValidacao({});
      setCamposInvalidos({});
      setDocumentoDigital(null);
      setInfosBoleto(null);

      if (isInativa) {
        setActiveTab('historico');
        carregarDetalhes();
        carregarHistorico();
      } else {
        setActiveTab('formulario');
        carregarDetalhes();
        carregarHistorico();
        carregarDocumentoDigital();
        carregarInfosBoleto();
      }
    }
  }, [show, solicitacao]);

  const carregarDetalhes = async () => {
    if (!solicitacao) return;
    setIsLoadingDetalhes(true);
    try {
      const data = await api.obterDetalhesCessao(solicitacao.codigo);
      setDetalhes(data);

      try {
        const conferencia = await api.obterConferenciaCamposCessao(
          solicitacao.codigo
        );
        if (conferencia && Array.isArray(conferencia)) {
          const docsValidacao = {};
          conferencia.forEach(campo => {
            if (campo.tipo_registro === 'DOCUMENTO' && campo.codigo_documento) {
              if (campo.valido !== null && campo.valido !== undefined) {
                docsValidacao[campo.codigo_documento] = campo.valido;
              }
            }
          });
          if (Object.keys(docsValidacao).length > 0) {
            setDocumentosValidacao(docsValidacao);
          }

          const conferenciasCarregadas = {};
          const camposInvalidosCarregados = {};
          conferencia.forEach(campo => {
            if (campo.tipo_registro !== 'DOCUMENTO') {
              const chave = montarChaveCampo(campo, data);
              if (chave) {
                if (campo.valido !== null && campo.valido !== undefined) {
                  conferenciasCarregadas[chave] = true;

                  if (campo.valido === false) {
                    camposInvalidosCarregados[chave] = true;
                  }
                }
              }
            }
          });
          if (Object.keys(conferenciasCarregadas).length > 0) {
            setConferencias(conferenciasCarregadas);
          }
          if (Object.keys(camposInvalidosCarregados).length > 0) {
            setCamposInvalidos(camposInvalidosCarregados);
          }
        }
      } catch (errConf) {
        console.log('Conferência não carregada:', errConf);
      }
    } catch (err) {
      setDetalhes(null);
      toast.error('Erro ao carregar detalhes da solicitação');
    } finally {
      setIsLoadingDetalhes(false);
    }
  };

  const montarChaveCampo = (campo, detalhesData) => {
    if (!campo || !detalhesData) return null;

    const mapaInversoCampos = {
      Nome: '_nome',
      CPF: '_cpf',
      RG: '_rg',
      EstadoCivil: '_estado_civil',
      DataNascimento: '_data_nascimento',
      Profissao: '_profissao',
      Nacionalidade: '_nacionalidade',
      Email: '_email',
      Telefone: '_telefone',
      Endereco: '_endereco',
      NomeConjuge: '_conjuge',
    };

    const mapaInversoLote = {
      Empreendimento: 'lote_empreendimento',
      Quadra: 'lote_quadra',
      Lote: 'lote_lote',
      AreaTotal: 'lote_area_total',
      Cidade: 'lote_cidade',
      TipoCessao: 'lote_tipo_cessao',
    };

    const mapaInversoSolicitacao = {
      Possui_Ligacao_Agua: 'solicitacao_ligacao_agua',
      Possui_Construcao: 'solicitacao_construcao',
    };

    if (campo.tipo_registro === 'LOTE') {
      return mapaInversoLote[campo.nome_campo] || null;
    }

    if (campo.tipo_registro === 'SOLICITACAO') {
      return mapaInversoSolicitacao[campo.nome_campo] || null;
    }

    if (
      campo.tipo_registro === 'CEDENTE' ||
      campo.tipo_registro === 'CESSIONARIO'
    ) {
      const tipo =
        campo.tipo_registro === 'CEDENTE' ? 'cedente' : 'cessionario';
      const pessoas =
        tipo === 'cedente' ? detalhesData.cedentes : detalhesData.cessionarios;
      if (pessoas && Array.isArray(pessoas)) {
        const pessoa = pessoas.find(p => normalizeCpfCnpj(p.cpf) === normalizeCpfCnpj(campo.documento));
        if (pessoa) {
          const sufixo = mapaInversoCampos[campo.nome_campo];
          if (sufixo) {
            return `${tipo}_${pessoa.codigo}${sufixo}`;
          }
        }
      }
    }

    return null;
  };

  const carregarHistorico = async () => {
    if (!solicitacao) return;
    setIsLoadingHistorico(true);
    try {
      const data = await api.obterHistoricoCessao(solicitacao.codigo);
      setHistorico(data || []);
      if (data && data.length > 0) {
        setSelectedHistorico(data[0]);
      }
    } catch (err) {
      setHistorico([]);
      toast.error('Erro ao carregar histórico');
    } finally {
      setIsLoadingHistorico(false);
    }
  };

  const carregarDocumentoDigital = async () => {
    if (!solicitacao) return;
    setIsLoadingDocumento(true);
    try {
      const data = await obterDocumentoDigitalCessao(solicitacao.codigo);
      setDocumentoDigital(data);
    } catch (err) {
      setDocumentoDigital(null);
      console.log('Documento digital não disponível:', err);
    } finally {
      setIsLoadingDocumento(false);
    }
  };

  const carregarInfosBoleto = async () => {
    if (!solicitacao) return;
    setIsLoadingInfosBoleto(true);
    try {
      const data = await cessaoInfosBoleto(solicitacao.codigo);
      setInfosBoleto(data);
    } catch (err) {
      setInfosBoleto(null);
      console.log('Infos boleto não disponíveis:', err);
    } finally {
      setIsLoadingInfosBoleto(false);
    }
  };

  const handleDesbloquear = async () => {
    if (!solicitacao) return;
    setIsDesbloqueando(true);
    try {
      await desbloquearSolicitacaoCessao({
        codigo_solicitacao: solicitacao.codigo,
      });
      toast.success('Solicitação desbloqueada com sucesso!');
      carregarDetalhes();
      carregarHistorico();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao desbloquear solicitação');
    } finally {
      setIsDesbloqueando(false);
    }
  };

  const abrirDocumentoPDF = () => {
    if (!documentoDigital) {
      toast.warning('Documento não disponível');
      return;
    }

    if (documentoDigital.url_documento) {
      window.open(documentoDigital.url_documento, '_blank');
      return;
    }

    if (documentoDigital.documento_pdf && documentoDigital.documento_pdf.length > 0) {
      try {
        const base64String = documentoDigital.documento_pdf;
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (err) {
        toast.error('Erro ao abrir o documento PDF');
        console.error('Erro ao processar PDF:', err);
      }
    } else {
      toast.warning('Documento PDF não disponível');
    }
  };

  const handleConferir = (key, value) => {
    setConferencias(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleVerAnexo = async documento => {
    if (!documento || !documento.codigo) {
      toast.warning('Documento não disponível');
      return;
    }
    try {
      const response = await downloadDocumentoCessao(documento.codigo);
      const url = window.URL.createObjectURL(response);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Erro ao abrir documento:', err);
      toast.error('Erro ao abrir o documento');
    }
  };

  const handleValidarDocumento = (codigoDocumento, valido) => {
    setDocumentosValidacao(prev => ({
      ...prev,
      [codigoDocumento]: valido,
    }));
  };

  const handleMarcarInvalido = (campoKey, invalido) => {
    setCamposInvalidos(prev => ({
      ...prev,
      [campoKey]: invalido,
    }));
  };

  const montarCamposConferencia = () => {
    const campos = [];

    const processarPessoa = (pessoa, tipo) => {
      if (!pessoa) return;
      const baseKey = tipo + '_' + (pessoa.codigo || 0);
      const cpfPessoa = normalizeCpfCnpj(pessoa.cpf);

      const camposPessoa = [
        { key: '_nome', nome: 'Nome' },
        { key: '_cpf', nome: 'CPF' },
      ];

      if (pessoa.tipo_pessoa !== '1') {
        camposPessoa.push(
          { key: '_rg', nome: 'RG' },
          { key: '_estado_civil', nome: 'EstadoCivil' },
          { key: '_data_nascimento', nome: 'DataNascimento' },
          { key: '_profissao', nome: 'Profissao' },
          { key: '_nacionalidade', nome: 'Nacionalidade' }
        );
      }

      camposPessoa.push(
        { key: '_email', nome: 'Email' },
        { key: '_telefone', nome: 'Telefone' },
        { key: '_endereco', nome: 'Endereco' }
      );

      if (pessoa.nome_conjuge) {
        camposPessoa.push({ key: '_conjuge', nome: 'NomeConjuge' });
      }

      camposPessoa.forEach(({ key, nome }) => {
        const fullKey = baseKey + key;
        const foiConferido = conferencias[fullKey] === true;
        const foiMarcadoInvalido = camposInvalidos[fullKey] === true;
        let valido = null;
        if (foiConferido) {
          valido = foiMarcadoInvalido ? false : true;
        }

        const tipoRegistro = tipo === 'cedente' ? 'CEDENTE' : 'CESSIONARIO';
        campos.push({
          tipo_registro: tipoRegistro,
          documento: cpfPessoa,
          codigo_documento: null,
          nome_campo: nome,
          valido: valido,
          editavel_cliente: campoEditavelCliente(nome, tipoRegistro),
        });
      });
    };

    (detalhes ? detalhes.cedentes : []).forEach(pessoa =>
      processarPessoa(pessoa, 'cedente')
    );
    (detalhes ? detalhes.cessionarios : []).forEach(pessoa =>
      processarPessoa(pessoa, 'cessionario')
    );

    const camposLote = [
      { key: 'lote_empreendimento', nome: 'Empreendimento' },
      { key: 'lote_quadra', nome: 'Quadra' },
      { key: 'lote_lote', nome: 'Lote' },
      { key: 'lote_area_total', nome: 'AreaTotal' },
      { key: 'lote_cidade', nome: 'Cidade' },
      { key: 'lote_tipo_cessao', nome: 'TipoCessao' },
    ];

    camposLote.forEach(({ key, nome }) => {
      const foiConferido = conferencias[key] === true;
      const foiMarcadoInvalido = camposInvalidos[key] === true;

      let valido = null;
      if (foiConferido) {
        valido = foiMarcadoInvalido ? false : true;
      }

      campos.push({
        tipo_registro: 'LOTE',
        documento: null,
        codigo_documento: null,
        nome_campo: nome,
        valido: valido,
        editavel_cliente: campoEditavelCliente(nome, 'LOTE'),
      });
    });
    const camposSolicitacao = [
      { key: 'solicitacao_ligacao_agua', nome: 'Possui_Ligacao_Agua' },
      { key: 'solicitacao_construcao', nome: 'Possui_Construcao' },
    ];

    camposSolicitacao.forEach(({ key, nome }) => {
      const foiConferido = conferencias[key] === true;
      const foiMarcadoInvalido = camposInvalidos[key] === true;

      let valido = null;
      if (foiConferido) {
        valido = foiMarcadoInvalido ? false : true;
      }

      campos.push({
        tipo_registro: 'SOLICITACAO',
        documento: null,
        codigo_documento: null,
        nome_campo: nome,
        valido: valido,
        editavel_cliente: campoEditavelCliente(nome, 'SOLICITACAO'),
      });
    });

    if (detalhes && detalhes.documentos) {
      detalhes.documentos.forEach(doc => {
        const validacaoLocal = documentosValidacao[doc.codigo];
        const docValido = validacaoLocal !== undefined ? validacaoLocal : null;

        campos.push({
          tipo_registro: 'DOCUMENTO',
          documento: doc.cpf ? normalizeCpfCnpj(doc.cpf) : null,
          codigo_documento: doc.codigo,
          nome_campo: doc.tipo_documento_descricao || 'Documento',
          valido: docValido,
          editavel_cliente: campoEditavelCliente(doc.tipo_documento_descricao || 'Documento', 'DOCUMENTO'),
        });
      });
    }

    return campos;
  };

  const handleSalvarProgresso = async () => {
    if (!solicitacao) return;

    const camposConferencia = montarCamposConferencia();
    if (camposConferencia.length === 0) {
      toast.warning('Nenhum campo foi conferido ainda.');
      return;
    }

    setIsSavingProgress(true);
    try {
      await api.salvarConferenciaCamposCessao({
        codigo_solicitacao: solicitacao.codigo,
        campos: camposConferencia,
        observacao: 'Progresso da conferência salvo.',
      });

      toast.success('Progresso salvo com sucesso!');
      carregarHistorico();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao salvar progresso');
    } finally {
      setIsSavingProgress(false);
    }
  };

  const handleSubmitDecisao = async () => {
    if (!solicitacao || !decisao) return;

    if (decisao === 3 && !motivoReprovacao) {
      toast.error('Selecione o motivo da reprovação');
      return;
    }

    if (decisao === 2 && !todosConferidos()) {
      toast.error('Para aprovar, é necessário conferir todos os campos.');
      return;
    }

    if (decisao === 2 && !todosDocumentosValidados()) {
      toast.error('Para aprovar, é necessário validar todos os documentos.');
      return;
    }

    if (decisao === 2 && !todosDocumentosAprovados()) {
      toast.error('Para aprovar, todos os documentos devem estar aprovados. Existem documentos reprovados.');
      return;
    }

    if (!observacao.trim()) {
      toast.error('Preencha a observação');
      return;
    }

    setIsSubmitting(true);
    try {
      const motivoEncontrado = MOTIVOS_REPROVACAO.find(
        m => m.value === motivoReprovacao
      );
      const obsCompleta =
        decisao === 3
          ? `${
              motivoEncontrado ? motivoEncontrado.label : motivoReprovacao
            }. ${observacao}`
          : observacao;

      const documentosValidacaoArray = Object.entries(documentosValidacao).map(
        ([codigo, valido]) => ({
          codigo_documento: parseInt(codigo, 10),
          valido: valido,
        })
      );
      const camposConferencia = montarCamposConferencia();
      let bloqueadoAutomaticamente = false;
      if (camposConferencia.length > 0) {
        const resConferencia = await api.salvarConferenciaCamposCessao({
          codigo_solicitacao: solicitacao.codigo,
          campos: camposConferencia,
          observacao: obsCompleta,
        });
        if (resConferencia && resConferencia.bloqueado_automaticamente) {
          bloqueadoAutomaticamente = true;
          toast.warning(
            'Solicitação bloqueada automaticamente: campos inválidos não editáveis pelo cliente.'
          );
          onHide();
          if (onStatusChange) onStatusChange(false);
          if (onSuccess) onSuccess();
          return;
        }
      }

      await api.alterarStatusCessao({
        codigo_solicitacao: solicitacao.codigo,
        codigo_etapa: solicitacao.codigo_etapa,
        codigo_sub_etapa: decisao,
        observacao: obsCompleta,
        enviar_email: enviarEmail,
        documentos_validacao: documentosValidacaoArray,
      });

      toast.success(
        decisao === 2
          ? 'Documentação aprovada com sucesso!'
          : 'Documentação reprovada com sucesso!'
      );
      onHide();
      if (onStatusChange) onStatusChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao alterar status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const todosConferidos = () => {
    const totalParaConferir = calcularTotalParaConferir();
    const totalConferidos = calcularTotalConferidos();
    return totalParaConferir > 0 && totalConferidos >= totalParaConferir;
  };

  const calcularCamposInvalidos = () => {
    if (!detalhes) return 0;

    let invalidos = 0;
    const totalParaConferir = calcularTotalParaConferir();
    const totalConferidos = calcularTotalConferidos();

    invalidos += totalParaConferir - totalConferidos;

    invalidos += Object.values(camposInvalidos).filter(v => v === true).length;

    return invalidos;
  };

  const temCamposInvalidosNaoEditaveis = () => {
    if (!detalhes) return false;

    const verificarPessoa = (pessoa, tipo) => {
      if (!pessoa) return false;
      const baseKey = tipo + '_' + (pessoa.codigo || 0);
      const camposPessoa = ['_nome', '_cpf', '_email', '_telefone'];

      if (pessoa.tipo_pessoa !== '1') {
        camposPessoa.push(
          '_rg',
          '_estado_civil',
          '_data_nascimento',
          '_profissao',
          '_nacionalidade'
        );
      }
      if (pessoa.nome_conjuge) {
        camposPessoa.push('_conjuge');
      }

      return camposPessoa.some(key => {
        const fullKey = baseKey + key;
        const foiMarcadoInvalido = camposInvalidos[fullKey] === true;
        return foiMarcadoInvalido;
      });
    };

    const cedentesInvalidos = (detalhes.cedentes || []).some(p =>
      verificarPessoa(p, 'cedente')
    );
    if (cedentesInvalidos) return true;

    const cessionariosInvalidos = (detalhes.cessionarios || []).some(p =>
      verificarPessoa(p, 'cessionario')
    );
    if (cessionariosInvalidos) return true;

    const camposLote = [
      'lote_empreendimento',
      'lote_quadra',
      'lote_lote',
      'lote_area_total',
      'lote_cidade',
      'lote_tipo_cessao',
    ];
    const loteInvalido = camposLote.some(key => {
      const foiMarcadoInvalido = camposInvalidos[key] === true;
      return foiMarcadoInvalido;
    });

    if (loteInvalido) return true;

    const camposSolicitacao = getSolicitacaoKeys();
    const solicitacaoInvalido = camposSolicitacao.some(key => {
      const foiMarcadoInvalido = camposInvalidos[key] === true;
      return foiMarcadoInvalido;
    });

    return solicitacaoInvalido;
  };

  const todosDocumentosValidados = () => {
    if (!detalhes || !detalhes.documentos) return true;
    const totalDocumentos = detalhes.documentos.length;
    if (totalDocumentos === 0) return true;

    return detalhes.documentos.every(doc => {
      return documentosValidacao[doc.codigo] !== undefined;
    });
  };

  const todosDocumentosAprovados = () => {
    if (!detalhes || !detalhes.documentos) return true;
    const totalDocumentos = detalhes.documentos.length;
    if (totalDocumentos === 0) return true;

    return detalhes.documentos.every(doc => {
      return documentosValidacao[doc.codigo] === true;
    });
  };

  const calcularTotalDocumentosValidados = () => {
    if (!detalhes || !detalhes.documentos) return 0;

    return detalhes.documentos.filter(doc => {
      return documentosValidacao[doc.codigo] !== undefined;
    }).length;
  };

  const podeAprovarReprovar = () => {
    return (
      detalhes && detalhes.codigo_sub_etapa === 1 && detalhes.codigo_etapa === 2
    );
  };

  const getStatusBadgeVariant = codigoSubEtapa => {
    switch (codigoSubEtapa) {
      case 2:
        return 'success';
      case 3:
        return 'danger';
      case 5:
        return 'info';
      default:
        return 'warning';
    }
  };

  const calcularTotalConferidos = () => {
    return Object.values(conferencias).filter(v => v === true).length;
  };

  const calcularTotalParaConferir = () => {
    if (!detalhes) return 0;
    let total = 0;
    (detalhes.cedentes || []).forEach(p => {
      total += getPessoaKeys(p, 'cedente').length;
    });
    (detalhes.cessionarios || []).forEach(p => {
      total += getPessoaKeys(p, 'cessionario').length;
    });
    if (detalhes.lote) {
      total += getLoteKeys().length;
    }
    total += getSolicitacaoKeys().length;
    return total;
  };

  if (!solicitacao) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaClipboardList className="mr-2" />
          Detalhes da Solicitação #{solicitacao.codigo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          backgroundColor: '#f8f9fa',
        }}
      >
        <div className="mb-3 p-3 bg-white rounded border shadow-sm">
          <Row>
            <Col md={4}>
              <small className="text-muted">Solicitante</small>
              <p className="mb-1 font-weight-bold">
                {solicitacao.nome_solicitante}
              </p>
            </Col>
            <Col md={4}>
              <small className="text-muted">CPF/CNPJ</small>
              <p className="mb-1">{formatCpfCnpj(solicitacao.cpf_cnpj)}</p>
            </Col>
            <Col md={4}>
              <small className="text-muted">Tipo Cessão</small>
              <p className="mb-1">{solicitacao.tipo_cessao}</p>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={3}>
              <small className="text-muted">Empreendimento</small>
              <p className="mb-1">{solicitacao.empreendimento}</p>
            </Col>
            <Col md={3}>
              <small className="text-muted">Quadra / Lote</small>
              <p className="mb-1">
                {solicitacao.quadra} / {solicitacao.lote}
              </p>
            </Col>
            <Col md={3}>
              <small className="text-muted">Etapa Atual</small>
              <p className="mb-1">
                <Badge variant="info">{solicitacao.etapa}</Badge>
              </p>
            </Col>
            <Col md={3}>
              <small className="text-muted">Status</small>
              <p className="mb-1">
                <Badge
                  variant={getStatusBadgeVariant(solicitacao.codigo_sub_etapa)}
                >
                  {solicitacao.sub_etapa}
                </Badge>
                {solicitacao.bloqueado && (
                  <Badge variant="danger" className="ml-2">
                    <FaLock size={10} /> Bloqueado
                  </Badge>
                )}
              </p>
            </Col>
          </Row>
          {(infosBoleto || isLoadingInfosBoleto) && (
            <Row className="mt-2">
              <Col md={6}>
                <small className="text-muted">Tipo da Taxa</small>
                <p className="mb-1">
                  {isLoadingInfosBoleto ? (
                    <FaSpinner className="fa-spin" />
                  ) : infosBoleto && infosBoleto.tipo_calculo ? (
                    <Badge variant="secondary">
                      {infosBoleto.taxa_boleto_tipo_calculo === 'F'
                        ? 'Valor Fixo'
                        : infosBoleto.taxa_boleto_tipo_calculo === 'P'
                        ? 'Percentual'
                        : 'Empreendimento'}
                    </Badge>
                  ) : (
                    '-'
                  )}
                  {infosBoleto && infosBoleto.descricao_taxa && (
                    <small className="ml-2 text-muted">
                      {limparZerosPercentual(infosBoleto.descricao_taxa)}
                    </small>
                  )}
                </p>
              </Col>
              <Col md={6}>
                <small className="text-muted">Valor da Taxa</small>
                <p className="mb-1 font-weight-bold">
                  {isLoadingInfosBoleto ? (
                    <FaSpinner className="fa-spin" />
                  ) : infosBoleto && infosBoleto.valor_taxa_apurado != null ? (
                    <span className="text-success">
                      <FaMoneyBillWave className="mr-1" />
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(infosBoleto.valor_taxa_apurado)}
                    </span>
                  ) : (
                    '-'
                  )}
                </p>
              </Col>
            </Row>
          )}
        </div>

        {isInativa && (
          <Alert variant="danger" className="mb-3">
            <FaLock className="mr-2" />
            <strong>Solicitação Inativa.</strong> Esta solicitação foi inativada. Apenas o histórico está disponível para visualização.
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={k => {
            if (isInativa && k !== 'historico' && k !== 'formulario') return;
            setActiveTab(k);
          }}
          className="mb-3"
        >
          <Tab
            eventKey="formulario"
            title={
              <>
                <FaFileAlt className="mr-1" /> Formulário
                {!isInativa && calcularTotalParaConferir() > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {calcularTotalConferidos()}/{calcularTotalParaConferir()}
                  </Badge>
                )}
              </>
            }
          >
            {isLoadingDetalhes ? (
              <div className="text-center py-4">
                <Loading />
              </div>
            ) : detalhes ? (
              <div className="p-2">
                <div style={isInativa ? { pointerEvents: 'none' } : undefined}>
                <Accordion defaultActiveKey={isInativa ? 'cedentes' : undefined} className="mb-3">
                  <Card>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey="cedentes"
                      className="cursor-pointer bg-light"
                    >
                      <strong>
                        <FaUsers className="mr-2" />
                        Cedente(s) - Atual Proprietário
                      </strong>
                      <Badge variant="primary" className="ml-2">
                        {(detalhes.cedentes || []).length}
                      </Badge>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="cedentes">
                      <Card.Body className="p-2">
                        {(detalhes.cedentes || []).map((pessoa, idx) => (
                          <SecaoPessoa
                            key={'cedente_' + idx}
                            pessoa={pessoa}
                            titulo={
                              pessoa.tipo === 'Principal'
                                ? 'Cedente Principal'
                                : pessoa.nome
                            }
                            tipo="cedente"
                            conferencias={conferencias}
                            onConferir={handleConferir}
                            onVerAnexo={handleVerAnexo}
                            documentosValidacao={documentosValidacao}
                            onValidarDocumento={handleValidarDocumento}
                            disabled={isSubmitting}
                            camposInvalidos={camposInvalidos}
                            onMarcarInvalido={handleMarcarInvalido}
                          />
                        ))}
                        {(detalhes.cedentes || []).length === 0 && (
                          <p className="text-muted text-center py-3">
                            Nenhum cedente encontrado
                          </p>
                        )}
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>

                <Accordion defaultActiveKey={isInativa ? 'cessionarios' : undefined} className="mb-3">
                  <Card>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey="cessionarios"
                      className="cursor-pointer bg-light"
                    >
                      <strong>
                        <FaUsers className="mr-2" />
                        Cessionário(s) - Novo Proprietário
                      </strong>
                      <Badge variant="success" className="ml-2">
                        {(detalhes.cessionarios || []).length}
                      </Badge>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="cessionarios">
                      <Card.Body className="p-2">
                        {(detalhes.cessionarios || []).map((pessoa, idx) => (
                          <SecaoPessoa
                            key={'cessionario_' + idx}
                            pessoa={pessoa}
                            titulo={
                              pessoa.tipo === 'Principal'
                                ? 'Cessionário Principal'
                                : pessoa.nome
                            }
                            tipo="cessionario"
                            conferencias={conferencias}
                            onConferir={handleConferir}
                            onVerAnexo={handleVerAnexo}
                            documentosValidacao={documentosValidacao}
                            onValidarDocumento={handleValidarDocumento}
                            disabled={isSubmitting}
                            camposInvalidos={camposInvalidos}
                            onMarcarInvalido={handleMarcarInvalido}
                          />
                        ))}
                        {(detalhes.cessionarios || []).length === 0 && (
                          <p className="text-muted text-center py-3">
                            Nenhum cessionário encontrado
                          </p>
                        )}
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>

                <SecaoLote
                  lote={detalhes.lote}
                  conferencias={conferencias}
                  onConferir={handleConferir}
                  onVerAnexo={handleVerAnexo}
                  documentosValidacao={documentosValidacao}
                  onValidarDocumento={handleValidarDocumento}
                  disabled={isSubmitting}
                  camposInvalidos={camposInvalidos}
                  onMarcarInvalido={handleMarcarInvalido}
                />

                <SecaoSolicitacao
                  detalhes={detalhes}
                  conferencias={conferencias}
                  onConferir={handleConferir}
                  camposInvalidos={camposInvalidos}
                  onMarcarInvalido={handleMarcarInvalido}
                />
                </div>

                {!isInativa && (<>
                <Card className="mt-4 mb-3">
                  <Card.Header className="bg-secondary text-white py-2">
                    <strong>
                      <FaClipboardList className="mr-2" />
                      Resumo da Conferência
                    </strong>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div
                          className={`text-center p-3 rounded ${
                            todosDocumentosValidados() &&
                            (detalhes.documentos || []).length > 0
                              ? 'bg-success text-white'
                              : 'bg-light'
                          }`}
                        >
                          <h4 className="mb-0">
                            {calcularTotalDocumentosValidados()}/
                            {(detalhes.documentos || []).length}
                          </h4>
                          <small
                            className={
                              todosDocumentosValidados() &&
                              (detalhes.documentos || []).length > 0
                                ? 'text-white'
                                : 'text-muted'
                            }
                          >
                            Documentos Verificados
                          </small>
                          {todosDocumentosValidados() &&
                            (detalhes.documentos || []).length > 0 && (
                              <div className="mt-1">
                                <FaCheck /> OK
                              </div>
                            )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div
                          className={`text-center p-3 rounded ${
                            calcularCamposInvalidos() > 0
                              ? 'bg-danger text-white'
                              : 'bg-light'
                          }`}
                        >
                          <h4 className="mb-0">{calcularCamposInvalidos()}</h4>
                          <small
                            className={
                              calcularCamposInvalidos() > 0
                                ? 'text-white'
                                : 'text-muted'
                            }
                          >
                            Campos Inválidos
                          </small>
                          {calcularCamposInvalidos() > 0 && (
                            <div className="mt-1">
                              <FaTimes /> Atenção
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    {temCamposInvalidosNaoEditaveis() && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        <FaLock className="mr-2" />
                        <strong>Atenção:</strong> Existem campos inválidos que
                        NÃO podem ser editados pelo cliente. A solicitação será{' '}
                        <strong>bloqueada automaticamente</strong> ao reprovar.
                      </Alert>
                    )}
                    <div className="mt-3 text-center">
                      <Button
                        variant="outline-primary"
                        onClick={handleSalvarProgresso}
                        disabled={isSavingProgress || isSubmitting || calcularTotalConferidos() === 0}
                      >
                        {isSavingProgress ? (
                          <>
                            <FaSpinner className="fa-spin mr-2" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <FaFileAlt className="mr-2" />
                            Salvar Progresso
                          </>
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {podeAprovarReprovar() ? (
                  <Card className="border-primary">
                    <Card.Header className="bg-primary text-white py-2">
                      <strong>
                        <FaCheckSquare className="mr-2" />
                        Decisão do Especialista
                      </strong>
                    </Card.Header>
                    <Card.Body>
                      {!todosConferidos() && (
                        <Alert variant="warning" className="mb-3">
                          <FaSquare className="mr-2" />
                          <strong>Campos pendentes:</strong> Confira todos os
                          campos antes de aprovar. Faltam{' '}
                          {calcularTotalParaConferir() -
                            calcularTotalConferidos()}{' '}
                          campo(s).
                        </Alert>
                      )}

                      {!todosDocumentosValidados() &&
                        (detalhes.documentos || []).length > 0 && (
                          <Alert variant="warning" className="mb-3">
                            <FaFile className="mr-2" />
                            <strong>Documentos pendentes:</strong> Valide todos
                            os documentos (aprovar ou reprovar) antes de tomar a
                            decisão. Faltam{' '}
                            {(detalhes.documentos || []).length -
                              calcularTotalDocumentosValidados()}{' '}
                            documento(s).
                          </Alert>
                        )}

                      <Row className="mb-3">
                        <Col md={6}>
                          <Button
                            variant={
                              decisao === 2 ? 'success' : 'outline-success'
                            }
                            block
                            size="lg"
                            onClick={() => setDecisao(2)}
                            disabled={isSubmitting}
                          >
                            <FaCheck className="mr-2" />
                            Aprovar
                          </Button>
                        </Col>
                        <Col md={6}>
                          <Button
                            variant={
                              decisao === 3 ? 'danger' : 'outline-danger'
                            }
                            block
                            size="lg"
                            onClick={() => setDecisao(3)}
                            disabled={isSubmitting}
                          >
                            <FaTimes className="mr-2" />
                            Reprovar
                          </Button>
                        </Col>
                      </Row>

                      {decisao === 3 && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Motivo da Reprovação{' '}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Select
                            options={MOTIVOS_REPROVACAO}
                            value={MOTIVOS_REPROVACAO.find(
                              m => m.value === motivoReprovacao
                            )}
                            onChange={opt =>
                              setMotivoReprovacao(opt ? opt.value : '')
                            }
                            styles={{
                              ...customStyles,
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                            }}
                            components={{ DropdownIndicator }}
                            placeholder="Selecione o motivo..."
                            menuPortalTarget={document.body}
                            isDisabled={isSubmitting}
                          />
                        </Form.Group>
                      )}

                      {decisao && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Observação <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={observacao}
                              onChange={e => setObservacao(e.target.value)}
                              placeholder={
                                decisao === 2
                                  ? 'Descreva os detalhes da aprovação...'
                                  : 'Descreva os detalhes da reprovação...'
                              }
                              disabled={isSubmitting}
                            />
                          </Form.Group>

                          <Form.Check
                            type="checkbox"
                            id="check-email-decisao"
                            label="Enviar e-mail ao cliente com a atualização"
                            checked={enviarEmail}
                            onChange={e => setEnviarEmail(e.target.checked)}
                            disabled={isSubmitting}
                            className="mb-3"
                          />

                          <Button
                            variant={decisao === 2 ? 'success' : 'danger'}
                            size="lg"
                            block
                            onClick={handleSubmitDecisao}
                            disabled={
                              isSubmitting ||
                              !observacao.trim() ||
                              (decisao === 3 && !motivoReprovacao)
                            }
                          >
                            {isSubmitting ? (
                              <>
                                <FaSpinner className="fa-spin mr-2" />
                                Processando...
                              </>
                            ) : decisao === 2 ? (
                              <>
                                <FaCheck className="mr-2" />
                                Confirmar Aprovação
                              </>
                            ) : (
                              <>
                                <FaTimes className="mr-2" />
                                Confirmar Reprovação
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                ) : (
                  <Alert variant="info" className="mt-3">
                    <strong>Status:</strong> {detalhes.sub_etapa || 'N/A'}
                    <br />
                    <small className="text-muted">
                      {detalhes.codigo_etapa === 2
                        ? 'A solicitação não está em análise. Use o botão abaixo para alterar o status manualmente.'
                        : 'A alteração de status só está disponível na etapa de Análise de Documentos.'}
                    </small>
                    {detalhes.codigo_etapa === 2 && (
                      <div className="mt-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onStatusChange(true)}
                        >
                          <FaFileAlt className="mr-2" />
                          Alterar Status
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}
                </>)}
              </div>
            ) : (
              <div className="p-3 text-center bg-white rounded border">
                <p className="text-muted py-4">
                  Não foi possível carregar os detalhes da solicitação.
                </p>
              </div>
            )}
          </Tab>

          <Tab
            eventKey="historico"
            title={
              <>
                <FaHistory className="mr-1" /> Histórico
              </>
            }
          >
            <div className="bg-white rounded border">
              {isLoadingHistorico ? (
                <div className="text-center py-4">
                  <Loading />
                </div>
              ) : historico.length === 0 ? (
                <p className="text-center text-muted py-4">
                  Nenhum registro no histórico
                </p>
              ) : (
                <Row className="m-0">
                  <Col
                    md={5}
                    className="border-right p-0"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {historico.map((item, idx) => (
                      <div
                        key={item.codigo || idx}
                        className={`p-3 border-bottom cursor-pointer ${
                          selectedHistorico &&
                          selectedHistorico.codigo === item.codigo
                            ? 'bg-light'
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedHistorico(item)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <strong
                              className="d-block"
                              style={{ fontSize: '0.9rem' }}
                            >
                              {item.titulo}
                            </strong>
                            <small className="text-muted">
                              <FaClock size={10} className="mr-1" />
                              {moment(item.data_hora).format(
                                'DD/MM/YYYY HH:mm'
                              )}
                            </small>
                          </div>
                          <div className="text-right">
                            <small className="text-primary d-block">
                              <FaUser size={10} className="mr-1" />
                              {item.usuario || 'Sistema'}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Col>
                  <Col
                    md={7}
                    className="p-3"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    {selectedHistorico ? (
                      <div>
                        <h6 className="mb-3">{selectedHistorico.titulo}</h6>
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">
                            Data/Hora
                          </small>
                          <span>
                            {moment(selectedHistorico.data_hora).format(
                              'DD/MM/YYYY [às] HH:mm:ss'
                            )}
                          </span>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">
                            Usuário
                          </small>
                          <span>{selectedHistorico.usuario || 'Sistema'}</span>
                        </div>
                        {selectedHistorico.etapa && (
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1">
                              Etapa
                            </small>
                            <Badge variant="secondary">
                              {selectedHistorico.etapa}
                            </Badge>
                            {selectedHistorico.sub_etapa && (
                              <Badge
                                variant="outline-secondary"
                                className="ml-2"
                              >
                                {selectedHistorico.sub_etapa}
                              </Badge>
                            )}
                          </div>
                        )}
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">
                            Descrição
                          </small>
                          <p
                            className="mb-0 p-2 bg-white rounded border"
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {selectedHistorico.descricao || 'Sem descrição'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4">
                        Selecione um item para ver os detalhes
                      </p>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="termo"
            disabled={isInativa}
            title={
              <>
                <FaClipboardList className="mr-1" /> Visualizar Termo
              </>
            }
          >
            {isLoadingDocumento ? (
              <div className="p-3 bg-white rounded border text-center">
                <Loading />
                <p className="text-muted mt-2">Carregando documento...</p>
              </div>
            ) : (
              <div className="p-3 bg-white rounded border">
                <Row>
                  <Col md={6} className="border-right">
                    <div className="p-3">
                      <h6 className="mb-4 text-center">
                        <FaFileAlt className="mr-2" />
                        Documento Gerado
                      </h6>
                      <div className="text-center">
                        <Button
                          variant="primary"
                          size="lg"
                          className="mb-2"
                          onClick={abrirDocumentoPDF}
                          disabled={!documentoDigital}
                        >
                          <FaFilePdf className="mr-2" />
                          {documentoDigital && documentoDigital.nome_arquivo
                            ? documentoDigital.nome_arquivo
                            : 'Termo de Cessão de Transferência'}
                        </Button>
                        <br />
                        <small className="text-muted">
                          {documentoDigital
                            ? 'Clique para visualizar o arquivo'
                            : 'Documento não disponível'}
                        </small>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="p-3">
                      <h6 className="mb-4 text-center">
                        <FaFileAlt className="mr-2" />
                        Assinatura Digital Solicitada
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="thead-light">
                            <tr>
                              <th>Pré-Venda</th>
                              <th>Status</th>
                              <th className="text-center">ClickSign</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                {documentoDigital && documentoDigital.numero_pre_venda
                                  ? documentoDigital.numero_pre_venda
                                  : 'N/A'}
                              </td>
                              <td>
                                <Badge variant="info" style={{ fontSize: '0.75rem' }}>
                                  {(() => {
                                    if (!documentoDigital) return 'Pendente';
                                    switch (documentoDigital.status_documento) {
                                      case 0: return 'Contrato Físico';
                                      case 1: return 'Solicitado';
                                      case 2: return 'Aprovado';
                                      case 3: return 'Reprovado';
                                      case 4: return 'Descartado';
                                      case 5: return 'Aguardando Assinatura Cliente';
                                      case 6: return 'Aguardando Assinatura Setpar';
                                      case 7: return 'Cancelado';
                                      case 8: return 'Finalizado';
                                      case 9: return 'Enviado';
                                      case 10: return 'Implantado';
                                      case 11: return 'Gravado';
                                      default: return 'Pendente';
                                    }
                                  })()}
                                </Badge>
                              </td>
                              <td className="text-center">
                                {documentoDigital && documentoDigital.codigo_documento_digital ? (
                                  <a
                                    href={`/documentodigital?codDocDigital=${documentoDigital.codigo_documento_digital}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Abrir solicitação do documento digital"
                                  >
                                    <FaExternalLinkAlt className="text-primary" size={16} />
                                  </a>
                                ) : (
                                  <span className="text-muted" title="Link não disponível">
                                    <FaFileAlt size={16} />
                                  </span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        {!isInativa && solicitacao.bloqueado &&
          solicitacao.codigo_sub_etapa === 5 && (
            <Button
              variant="outline-danger"
              onClick={handleDesbloquear}
              disabled={isDesbloqueando || isSubmitting}
              className="d-inline-flex align-items-center mr-auto"
            >
              {isDesbloqueando ? (
                <>
                  <FaSpinner className="fa-spin mr-2" />
                  Desbloqueando...
                </>
              ) : (
                <>
                  <FaLockOpen className="mr-2" />
                  Desbloquear
                </>
              )}
            </Button>
          )}
        <Button
          variant="warning"
          onClick={onHide}
          className="d-inline-flex align-items-center"
        >
          <FaWindowClose className="mr-2" />
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDetalhes;
